import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
// use jsonLogic: http://jsonlogic.com/
import { formatDate, formatDateWithFormat, isValidDateWithFormat } from "../../lib/dates";
const logicalOperators = ['and', 'or'];
const defaultLogicalOperator = 'and';
export const getEmptyCondition = () => ({
  operator: null,
  field: null,
  value: null
});
export const getEmptyGroup = () => ({
  operator: defaultLogicalOperator,
  conditions: [getEmptyCondition()]
});
export const getEmptyConditions = () => ({
  operator: defaultLogicalOperator,
  groups: [getEmptyGroup()]
});
const mapCondition = (condition, listMap) => {
  var _condition$operator, _condition$operator2;
  const operator = Object.keys(condition)[0];
  const field = condition === null || condition === void 0 ? void 0 : (_condition$operator = condition[operator]) === null || _condition$operator === void 0 ? void 0 : _condition$operator[0];
  let value = condition === null || condition === void 0 ? void 0 : (_condition$operator2 = condition[operator]) === null || _condition$operator2 === void 0 ? void 0 : _condition$operator2[1];
  if (listMap && field !== null && field !== void 0 && field.var && listMap[field.var] && listMap[field.var][value]) value = {
    label: listMap[field.var][value],
    value
  };
  return {
    field: field && field.var ? field.var : null,
    operator,
    value
  };
};
const hasGroupsInside = conditions => conditions.some(cond => {
  const key = Object.keys(cond)[0];
  return logicalOperators.indexOf(key) !== -1;
});
const createGroups = (conditions, listMap) => {
  let groups = [];
  const operator = Object.keys(conditions)[0];
  if (logicalOperators.indexOf(operator) === -1) {
    // can be == , !=, >, < ... -> we only have one group
    groups.push({
      operator: defaultLogicalOperator,
      conditions: [mapCondition(conditions, listMap)]
    });
  } else {
    // can be "and" or "or"
    const auxCond = conditions[operator];
    if (hasGroupsInside(auxCond)) {
      auxCond.forEach(cond => {
        groups = groups.concat(createGroups(cond, listMap));
      });
    } else {
      groups.push({
        operator,
        conditions: auxCond.map(cond => mapCondition(cond, listMap))
      });
    }
  }
  return groups;
};
export const mapConditions = (conditions, listMap) => {
  if (!conditions) {
    return getEmptyConditions();
  }
  const groups = createGroups(conditions, listMap);
  const operator = groups.length > 1 ? Object.keys(conditions)[0] : defaultLogicalOperator;
  return {
    operator,
    groups
  };
};
const mapConditionToJsonLogic = (condition, fieldPrefix, deleteFieldPrefix) => {
  const {
    field,
    operator,
    value
  } = condition;
  let finalValue = value;

  // Multilist
  if (Array.isArray(value) && value.length) finalValue.map(v => v.value);

  // List
  if (value !== null && value !== void 0 && value.value) finalValue = value.value;

  // Date ISO UTC format
  if (finalValue && finalValue instanceof Date) finalValue = finalValue.toISOString();
  let varField = (field === null || field === void 0 ? void 0 : field.value) || field;
  if (fieldPrefix) {
    if (deleteFieldPrefix) {
      varField = varField.replace(`${fieldPrefix}.`, '');
    } else {
      varField = `${fieldPrefix}.${varField}`;
    }
  }
  return {
    [(operator === null || operator === void 0 ? void 0 : operator.value) || operator]: [{
      var: varField
    }, value && value.value ? value.value : value]
  };
};
const mapConditionsToJsonLogic = (conditions, fieldPrefix, deleteFieldPrefix) => conditions.map(condition => mapConditionToJsonLogic(condition, fieldPrefix, deleteFieldPrefix));
const validConditionFilter = cond => {
  const {
    field,
    operator: op,
    value
  } = cond;
  return !!(field && op && field && op && value !== null && value !== undefined);
};
const mapGroupToJsonLogic = (group, fieldPrefix, deleteFieldPrefix) => {
  const {
    operator: auxOp,
    conditions
  } = group;
  const auxCond = conditions.filter(validConditionFilter);
  if (auxCond.length === 0) {
    return {};
  }
  if (auxCond.length === 1) {
    return mapConditionToJsonLogic(conditions[0], fieldPrefix, deleteFieldPrefix);
  }
  return {
    [auxOp]: mapConditionsToJsonLogic(conditions, fieldPrefix, deleteFieldPrefix)
  };
};
export const mapToJsonLogic = function (_ref, fieldPrefix, deleteFieldPrefix) {
  let {
    operator,
    groups
  } = _ref;
  if (deleteFieldPrefix === void 0) {
    deleteFieldPrefix = false;
  }
  if (groups.length > 1) {
    const auxGroups = groups.map(group => mapGroupToJsonLogic(group, fieldPrefix, deleteFieldPrefix));
    return {
      [operator]: auxGroups
    };
  }
  if (groups.length === 1) {
    return mapGroupToJsonLogic(groups[0], fieldPrefix, deleteFieldPrefix);
  }
  return null;
};
export const getGroupConditionString = function (_ref2, locale, listMapFields) {
  let {
    operator = defaultLogicalOperator,
    conditions
  } = _ref2;
  if (listMapFields === void 0) {
    listMapFields = {};
  }
  const str = conditions.reduce((prevCond, cond) => {
    const {
      field,
      operator: op,
      value
    } = cond;
    let val = value && value.label ? value.label : value;
    if (value instanceof Date) val = formatDate(val, locale);
    if (!field && !op && !val && val !== 0) return prevCond;
    const finalField = listMapFields && listMapFields[field === null || field === void 0 ? void 0 : field.value] || (field === null || field === void 0 ? void 0 : field.value) || field;
    const finalVal = val !== null && val !== undefined ? val : '';
    return prevCond.concat([finalField || '', (op === null || op === void 0 ? void 0 : op.value) || op || '', finalVal].join(' '));
  }, []);
  return (str === null || str === void 0 ? void 0 : str.length) > 0 ? str === null || str === void 0 ? void 0 : str.join(` ${operator.toUpperCase()} `) : '';
};
export const getConditionsString = function (_ref3, locale, listMapFields) {
  let {
    operator,
    groups
  } = _ref3;
  if (listMapFields === void 0) {
    listMapFields = {};
  }
  const str = groups === null || groups === void 0 ? void 0 : groups.map(group => {
    const strGroup = getGroupConditionString(group, locale, listMapFields);
    if (strGroup.length > 0) {
      return `(${strGroup})`;
    }
    return null;
  }).filter(group => group !== null);
  return (str === null || str === void 0 ? void 0 : str.length) > 0 ? str === null || str === void 0 ? void 0 : str.join(` ${operator.toUpperCase()} `) : '';
};
const mapGroupValues = group => {
  if (!group || !group.conditions || group.conditions.length === 0) return [];
  return group.conditions.map(condition => {
    var _condition$field;
    const value = condition.value && condition.value.hasOwnProperty('value') ? condition.value.value : condition.value;
    return {
      [(_condition$field = condition.field) === null || _condition$field === void 0 ? void 0 : _condition$field.value]: value
    };
  });
};
export const mapFieldsValues = mapConditions => {
  if (!mapConditions || !mapConditions.groups || mapConditions.groups.length === 0) return [];
  return mapConditions.groups.reduce((obj, group) => {
    obj = [...obj, ...mapGroupValues(group)];
    return obj;
  }, []);
};
export const validateConditions = _ref4 => {
  let {
    groups
  } = _ref4;
  const failedGroups = groups === null || groups === void 0 ? void 0 : groups.find(group => {
    const {
      conditions
    } = group;
    const validConditions = conditions.filter(validConditionFilter);
    return validConditions.length !== conditions.length;
  });
  return !failedGroups;
};
export const transformDataConditions = (conditions, _ref5, isForOutsideComponent) => {
  let {
    fieldPrefix,
    dateFormat,
    locale = 'en-US'
  } = _ref5;
  conditions.groups = conditions.groups.map(group => {
    const newConditions = group.conditions.map(condition => {
      const {
        field,
        value
      } = condition;
      let finalField = field === null || field === void 0 ? void 0 : field.value;
      let finalValue = value;
      if (fieldPrefix) {
        if (isForOutsideComponent) {
          finalField = `${fieldPrefix}.${field === null || field === void 0 ? void 0 : field.value}`;
        } else {
          var _ref6;
          finalField = (_ref6 = (field === null || field === void 0 ? void 0 : field.value) || field) === null || _ref6 === void 0 ? void 0 : _ref6.replace(`${fieldPrefix}.`, '');
        }
      }
      if (dateFormat) {
        if (isForOutsideComponent && finalValue && finalValue instanceof Date) {
          finalValue = formatDate(finalValue, locale, dateFormat);
        } else if (!isForOutsideComponent && finalValue && isValidDateWithFormat(finalValue, locale, dateFormat)) {
          finalValue = formatDateWithFormat(finalValue, locale, dateFormat);
        }
      }
      return _objectSpread(_objectSpread({}, condition), {}, {
        field: finalField,
        value: finalValue
      });
    });
    return _objectSpread(_objectSpread({}, group), {}, {
      conditions: newConditions
    });
  });
  return conditions;
};